import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Navbar from './Navbar';
import Footer from './Footer';
import HoroscopeSection from '../Horoscope/HoroscopeSection';
import ZodiacWheel from '../UI/ZodiacWheel';
import './LandingPage.css';

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.astralight.xyz&hl=fr';

const faqItems = [
    {
        question: 'Comment calculer une synastrie gratuitement ?',
        answer: "Entrez les informations de naissance des deux personnes (date, heure et ville) dans notre calculateur de synastrie. Le calcul, les scores de compatibilité et l'interprétation sont gratuits et immédiats, sans créer de compte.",
    },
    {
        question: "L'interprétation de la synastrie est-elle vraiment incluse ?",
        answer: "Oui. Vous recevez cinq scores — attraction, communication, valeurs communes, gestion des conflits et potentiel à long terme — accompagnés d'une analyse rédigée des aspects entre vos deux thèmes. Pas un simple pourcentage.",
    },
    {
        question: "Faut-il connaître l'heure de naissance exacte ?",
        answer: "C'est préférable : l'heure détermine l'ascendant et les maisons, qui affinent l'analyse. Si vous ne la connaissez pas, la synastrie reste calculable à partir des positions planétaires du jour de naissance.",
    },
    {
        question: 'Quelle différence entre synastrie et compatibilité des signes ?',
        answer: "La compatibilité des signes ne compare que vos deux signes solaires, soit 144 combinaisons pour toute l'humanité. La synastrie superpose vos thèmes astraux complets et mesure les aspects réels entre vos planètes : c'est la méthode utilisée par les astrologues professionnels.",
    },
    {
        question: "Qu'est-ce que la synastrie karmique ?",
        answer: "On parle de synastrie karmique quand l'analyse insiste sur les planètes lentes et les points sensibles — Saturne, Pluton, Nœuds lunaires — qui évoquent les liens du destin entre deux personnes. Les aspects de Saturne et de Pluton sont intégrés à notre calcul, notamment dans le score de potentiel à long terme.",
    },
];

const LandingPage = () => {
    // Effet pour scroll vers le haut à l'arrivée sur la page
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const faqSchema = {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqItems.map((item) => ({
            '@type': 'Question',
            name: item.question,
            acceptedAnswer: { '@type': 'Answer', text: item.answer },
        })),
    };

    return (
        <>
            <Helmet>
                <title>Synastrie Gratuite avec Interprétation Complète | AstraLight</title>
                <meta name="description" content="Calculez votre synastrie de couple gratuitement : aspects entre vos deux thèmes astraux, scores de compatibilité et interprétation détaillée. Sans inscription, résultat immédiat." />
                <meta name="keywords" content="synastrie gratuite, synastrie couple, calcul synastrie, synastrie gratuite avec interprétation, compatibilité astrologique, thème astral, horoscope" />
                <link rel="canonical" href="https://astralight.xyz/" />

                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://astralight.xyz/" />
                <meta property="og:title" content="Synastrie Gratuite avec Interprétation Complète | AstraLight" />
                <meta property="og:description" content="Deux dates de naissance suffisent : comparez vos thèmes astraux et recevez vos scores de compatibilité avec une interprétation complète. Gratuit, sans inscription." />
                <meta property="og:image" content="https://astralight.xyz/images/astralight_og.webp" />
                <meta property="og:site_name" content="AstraLight" />

                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="Synastrie Gratuite avec Interprétation | AstraLight" />
                <meta name="twitter:description" content="Comparez vos deux thèmes astraux : scores de compatibilité et interprétation complète, gratuitement." />
                <meta name="twitter:image" content="https://astralight.xyz/images/astralight_og.webp" />

                {/* Schema.org pour Website */}
                <script type="application/ld+json">
                {`
                    {
                        "@context": "https://schema.org",
                        "@type": "WebSite",
                        "name": "AstraLight",
                        "url": "https://astralight.xyz/",
                        "description": "Outils d'astrologie gratuits : synastrie de couple avec interprétation, calcul de thème astral et horoscope hebdomadaire."
                    }
                `}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(faqSchema)}
                </script>
                <script type="application/ld+json">
                {`
                    {
                        "@context": "https://schema.org",
                        "@type": "MobileApplication",
                        "name": "Astralight",
                        "operatingSystem": "ANDROID",
                        "applicationCategory": "LifestyleApplication",
                        "installUrl": "${PLAY_STORE_URL}",
                        "offers": { "@type": "Offer", "price": "0", "priceCurrency": "EUR" }
                    }
                `}
                </script>
            </Helmet>

            <Navbar />

            <main className="landing-page">
                {/* ============ HERO ============ */}
                <section className="hero">
                    <div className="container">
                        <div className="hero-sky sky">
                            <ZodiacWheel className="hero-wheel" />

                            <div className="hero-content">
                                <p className="hero-eyebrow rise rise-1">
                                    <span className="star-accent">✦</span> L'astrologie du couple, prise au sérieux
                                </p>
                                <h1 className="rise rise-2">
                                    Synastrie gratuite : ce que le ciel dit vraiment de votre couple
                                </h1>
                                <p className="hero-subtitle rise rise-3">
                                    Deux dates de naissance suffisent. AstraLight superpose vos thèmes astraux,
                                    analyse chaque aspect entre vos planètes et vous livre vos scores de
                                    compatibilité avec une interprétation complète.
                                </p>
                                <div className="hero-buttons rise rise-4">
                                    <Link to="/compatibilite-amoureuse-astrologie" className="hero-button">
                                        <i className="fa-solid fa-heart" aria-hidden="true"></i>
                                        Calculer notre synastrie
                                    </Link>
                                    <Link to="/calcul-theme-astral" className="hero-button secondary">
                                        <i className="fa-solid fa-sun" aria-hidden="true"></i>
                                        Voir mon thème astral
                                    </Link>
                                </div>
                                <p className="hero-microproof rise rise-4">
                                    100&nbsp;% gratuit · Sans inscription · Résultat immédiat
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ============ PREUVES / DIFFÉRENCIATION ============ */}
                <section className="trust">
                    <div className="container">
                        <div className="trust-grid">
                            <div className="trust-item">
                                <i className="fa-solid fa-wand-magic-sparkles" aria-hidden="true"></i>
                                <h3>Interprétation incluse</h3>
                                <p>Pas juste un score : une vraie lecture, aspect par aspect, de votre relation.</p>
                            </div>
                            <div className="trust-item">
                                <i className="fa-solid fa-satellite-dish" aria-hidden="true"></i>
                                <h3>Calcul astronomique réel</h3>
                                <p>Positions planétaires issues des éphémérides suisses, la référence des professionnels.</p>
                            </div>
                            <div className="trust-item">
                                <i className="fa-solid fa-unlock" aria-hidden="true"></i>
                                <h3>Sans inscription</h3>
                                <p>Vos données de naissance servent au calcul, rien d'autre. Aucun compte requis.</p>
                            </div>
                            <div className="trust-item">
                                <i className="fa-solid fa-share-nodes" aria-hidden="true"></i>
                                <h3>Résultat partageable</h3>
                                <p>Chaque synastrie a son lien permanent. Envoyez-le à la personne concernée.</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ============ COMMENT ÇA MARCHE ============ */}
                <section className="steps">
                    <div className="container">
                        <h2>Le calcul de synastrie, en trois étapes</h2>
                        <div className="steps-grid">
                            <div className="step-card panel">
                                <span className="step-number">I</span>
                                <i className="fa-solid fa-user-pen step-icon" aria-hidden="true"></i>
                                <h3>Vos deux profils</h3>
                                <p>
                                    Prénoms, dates, heures et villes de naissance. L'heure exacte affine
                                    l'ascendant et les maisons — sans elle, l'analyse reste possible.
                                </p>
                            </div>
                            <div className="step-card panel">
                                <span className="step-number">II</span>
                                <i className="fa-solid fa-globe step-icon" aria-hidden="true"></i>
                                <h3>Vos deux ciels</h3>
                                <p>
                                    Nous dressons vos deux cartes du ciel, puis repérons chaque aspect qui relie
                                    vos planètes : conjonctions, trigones, carrés, oppositions.
                                </p>
                            </div>
                            <div className="step-card panel">
                                <span className="step-number">III</span>
                                <i className="fa-solid fa-scroll step-icon" aria-hidden="true"></i>
                                <h3>Votre interprétation</h3>
                                <p>
                                    Cinq scores — attraction, communication, valeurs, conflits, long terme —
                                    et une lecture détaillée de ce qui les explique.
                                </p>
                            </div>
                        </div>
                        <div className="steps-cta">
                            <Link to="/compatibilite-amoureuse-astrologie" className="hero-button">
                                Commencer le calcul
                                <i className="fa-solid fa-arrow-right" aria-hidden="true"></i>
                            </Link>
                        </div>
                    </div>
                </section>

                {/* ============ SECTION SEO SYNASTRIE ============ */}
                <section className="synastrie-seo">
                    <div className="container">
                        <div className="synastrie-seo-content panel">
                            <h2>La synastrie, bien plus qu'un test de compatibilité entre signes</h2>
                            <p>
                                Les tests qui comparent deux signes solaires ont un défaut : ils rangent
                                l'humanité entière dans 144 cases. La synastrie fait l'inverse. Elle superpose
                                vos deux <Link to="/calcul-theme-astral">thèmes astraux</Link> complets —{' '}
                                <Link to="/blog/planetes/soleil">Soleil</Link>,{' '}
                                <Link to="/blog/planetes/lune">Lune</Link>,{' '}
                                <Link to="/blog/planetes/venus">Vénus</Link>,{' '}
                                <Link to="/blog/planetes/mars">Mars</Link>, ascendant, maisons — et étudie les
                                angles précis que forment vos planètes entre elles.
                            </p>
                            <p>
                                Un trigone Vénus-Lune n'a pas le même goût qu'un carré Mars-Saturne. C'est cette
                                géométrie fine qui explique pourquoi deux signes « incompatibles » filent parfois
                                le parfait amour, et pourquoi deux signes réputés faits l'un pour l'autre
                                s'épuisent au quotidien.
                            </p>
                            <p>
                                Notre calculateur de <strong>synastrie gratuite avec interprétation</strong> vous
                                donne cette analyse en trente secondes : les aspects entre vos deux ciels, cinq
                                scores de compatibilité et une lecture rédigée de chacun d'eux.{' '}
                                <Link to="/compatibilite-amoureuse-astrologie">Essayez avec votre couple</Link> —
                                ou avec ce crush dont vous connaissez la date de naissance.
                            </p>
                        </div>
                    </div>
                </section>

                {/* ============ OUTILS ============ */}
                <section className="features">
                    <div className="container">
                        <h2>Quatre outils gratuits pour explorer votre ciel</h2>
                        <div className="features-grid">
                            <div className="feature-card panel">
                                <div className="feature-icon icon-synastrie">
                                    <i className="fa-solid fa-heart" aria-hidden="true"></i>
                                </div>
                                <h3>Synastrie de couple</h3>
                                <p>Comparez deux thèmes astraux : scores de compatibilité et interprétation aspect par aspect.</p>
                                <Link to="/compatibilite-amoureuse-astrologie" className="feature-link">Calculer une synastrie</Link>
                            </div>

                            <div className="feature-card panel">
                                <div className="feature-icon icon-theme">
                                    <i className="fa-solid fa-sun" aria-hidden="true"></i>
                                </div>
                                <h3>Thème astral</h3>
                                <p>Votre carte du ciel de naissance : planètes, signes, maisons et aspects, expliqués simplement.</p>
                                <Link to="/calcul-theme-astral" className="feature-link">Calculer mon thème</Link>
                            </div>

                            <div className="feature-card panel">
                                <div className="feature-icon icon-horoscope">
                                    <i className="fa-solid fa-moon" aria-hidden="true"></i>
                                </div>
                                <h3>Horoscope hebdomadaire</h3>
                                <p>Chaque semaine, l'influence des transits du moment pour les douze signes du zodiaque.</p>
                                <Link to="/horoscope-hebdomadaire" className="feature-link">Lire l'horoscope</Link>
                            </div>

                            <div className="feature-card panel">
                                <div className="feature-icon icon-blog">
                                    <i className="fa-solid fa-book-open" aria-hidden="true"></i>
                                </div>
                                <h3>Blog astrologique</h3>
                                <p>Planètes, signes, maisons : de quoi comprendre en profondeur ce que votre synastrie raconte.</p>
                                <Link to="/blog" className="feature-link">Explorer les articles</Link>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ============ APP ANDROID ============ */}
                <section className="app-section">
                    <div className="container">
                        <div className="app-panel sky">
                            <div className="app-content">
                                <p className="hero-eyebrow">
                                    <span className="star-accent">✦</span> Application Android
                                </p>
                                <h2>AstraLight tient aussi dans votre poche</h2>
                                <p className="app-description">
                                    Sur Android, vos profils et vos synastries restent sauvegardés. Calculez de
                                    nouveaux thèmes où que vous soyez et recevez un horoscope personnalisé, basé
                                    sur votre propre carte du ciel — pas seulement votre signe.
                                </p>
                                <ul className="app-features">
                                    <li><i className="fa-solid fa-users" aria-hidden="true"></i> Profils et synastries sauvegardés</li>
                                    <li><i className="fa-solid fa-wand-magic-sparkles" aria-hidden="true"></i> Horoscope personnalisé selon votre thème</li>
                                    <li><i className="fa-solid fa-download" aria-hidden="true"></i> Gratuite au téléchargement</li>
                                </ul>
                                <a
                                    href={PLAY_STORE_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="app-play-button"
                                    aria-label="Télécharger Astralight sur Google Play"
                                >
                                    <i className="fa-brands fa-google-play" aria-hidden="true"></i>
                                    <span>
                                        <small>Disponible sur</small>
                                        Google Play
                                    </span>
                                </a>
                            </div>
                            <div className="app-visual" aria-hidden="true">
                                <ZodiacWheel className="app-wheel" />
                                <i className="fa-solid fa-mobile-screen-button app-phone-icon"></i>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ============ FAQ ============ */}
                <section className="landing-faq">
                    <div className="container">
                        <div className="faq panel">
                            <h2>Questions fréquentes sur la synastrie gratuite</h2>
                            <div className="faq-items">
                                {faqItems.map((item, index) => (
                                    <div key={index} className="faq-item">
                                        <details>
                                            <summary>{item.question}</summary>
                                            <p>{item.answer}</p>
                                        </details>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </section>

                {/* ============ BLOG ============ */}
                <section className="blog-preview">
                    <div className="container">
                        <h2>Comprendre ce que votre synastrie raconte</h2>
                        <div className="blog-grid">
                            <article className="blog-card panel">
                                <img src="/images/planetes/venus.webp" alt="Vénus en astrologie" className="blog-image" loading="lazy" />
                                <div className="blog-content">
                                    <h3>Vénus en astrologie</h3>
                                    <p>La planète de l'amour : comment elle façonne vos attirances et votre manière d'aimer.</p>
                                    <Link to="/blog/planetes/venus" className="blog-link">Lire l'article</Link>
                                </div>
                            </article>

                            <article className="blog-card panel">
                                <img src="/images/planetes/lune.webp" alt="La Lune en astrologie" className="blog-image" loading="lazy" />
                                <div className="blog-content">
                                    <h3>La Lune en astrologie</h3>
                                    <p>Vos émotions et vos besoins profonds : le pilier silencieux de toute synastrie.</p>
                                    <Link to="/blog/planetes/lune" className="blog-link">Lire l'article</Link>
                                </div>
                            </article>

                            <article className="blog-card panel">
                                <img src="/images/maisons/maison7.png" alt="La maison 7 en astrologie" className="blog-image" loading="lazy" />
                                <div className="blog-content">
                                    <h3>La maison 7, celle du couple</h3>
                                    <p>Mariage, partenariats, engagements : la maison qui parle de l'autre dans votre thème.</p>
                                    <Link to="/blog/maisons/maison-7" className="blog-link">Lire l'article</Link>
                                </div>
                            </article>
                        </div>
                        <div className="blog-more">
                            <Link to="/blog" className="blog-more-link">Voir tous les articles</Link>
                        </div>
                    </div>
                </section>

                <HoroscopeSection />

                {/* ============ CTA FINAL ============ */}
                <section className="cta">
                    <div className="container">
                        <div className="cta-content sky">
                            <h2>Prêts à lire votre ciel à deux&nbsp;?</h2>
                            <p>Le calcul prend trente secondes. L'interprétation, elle, pourrait bien occuper toute votre soirée.</p>
                            <div className="cta-buttons">
                                <Link to="/compatibilite-amoureuse-astrologie" className="hero-button">
                                    <i className="fa-solid fa-heart" aria-hidden="true"></i>
                                    Calculer notre synastrie gratuite
                                </Link>
                            </div>
                            <p className="hero-microproof">Sans inscription — vos données de naissance ne servent qu'au calcul.</p>
                        </div>
                    </div>
                </section>
            </main>

            <Footer />
        </>
    );
};

export default LandingPage;
